import React, { FC, useRef, useState } from "react";
import styled from "styled-components";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/free-mode";
import contentfulComponentLoop from "../lib/contentfulComponentLoop";
import { Entry } from "contentful";
import { font } from "../lib/sharedStyles";
import { FreeMode, Navigation, Keyboard, Mousewheel } from "swiper/modules";
import DirectionalArrow from "./Icons/DirectionalArrow";

interface DynamicContentCarouselProps extends Entry {
  id: string;
  brand: string;
  fields: {
    title: string;
    slides: [];
    slideContent: [Entry];
    slideContentBackground: string;
  };
  maxWidth?: number;
}

const RootContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  width: auto;
  max-width: 100%;
  margin: 0 auto;
  padding: 20px 0;
`;

const CarouselWrapper = styled.div<{ $maxWidth?: number }>`
  display: flex;
  position: relative;
  width: auto;
  max-width: ${({ $maxWidth }) => ($maxWidth ? `${$maxWidth}px` : '125%')};
  margin: 0 auto;
  overflow: visible;
  margin-left: initial;
  margin-right: initial;
  margin-bottom: 20px;
  
  @media (min-width: 1024px) {
    max-width: ${({ $maxWidth }) => ($maxWidth ? `${$maxWidth}px` : '110%')};
  }

  .swiper-wrapper {
    max-width: 100%;
    box-sizing: border-box;
    overflow: visible;
    position: relative;
  }

  .swiper-slide {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 258.8px;
    transform: scale(0.85);
    background: transparent;
    will-change: transform, opacity;
    transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;

    img {
      width: 100%;
      height: auto;
      background: transparent;
      will-change: transform, opacity;
    }
  }

  figcaption {
    padding: 0;
    visibility: hidden;
    opacity: 0;
    will-change: opacity, transform;
    transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;

    h1, h2, h3, h4, p {
      text-transform: uppercase;
      font-size: 1.15rem;
      padding-top: 25px;
      margin-bottom: 0;
      text-align: center;

      @media (min-width: 1024px) {
        font-size: 18px;
        font-weight: 900;
        line-height: 30px;
      }
    }
  }

  .swiper-slide-active {
    transform: scale(1);
    opacity: 1;

    figcaption {
      visibility: visible;
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ContentContainer = styled.div<{ $activeIndex: number; $slideContentBackground: string }>`
  width: 100vw;
  background-color: ${({ $slideContentBackground }) => $slideContentBackground};
  background-repeat: no-repeat;
  background-position: center center;

  ${({ $activeIndex }) => `
    > * {
      display: none;

      &:nth-child(${$activeIndex + 1}) {
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 0 auto;
      }
    }
  `}
`;

const StyledTitle = styled.span`
  ${font("Futura, sans-serif", 900, "1.75rem", "2.25rem")}
  text-align: center;
  text-transform: uppercase;
  margin-bottom: 24px;

  @media (min-width: 1024px) {
    ${font("Futura, sans-serif", 900, "4rem", "5rem")}
    margin-bottom: 60px;
  }
`;

const ArrowContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 192px;
  z-index: 1;

  @media (min-width: 1024px) {
    gap: 256px;
  }
`;

const ArrowWrap = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  pointer-events: all;
`;

const RailArrow: FC<{ direction: string; railId: string }> = ({ direction, railId }) => (
  <ArrowWrap className={`swiper-button swiper-button-${direction}-${railId}`}>
    <DirectionalArrow
      direction={direction == 'left' ? 'left' : 'right'}
      fill="#000"
      width="24"
      height="24"
    />
  </ArrowWrap>
);

const DynamicContentCarousel: FC<DynamicContentCarouselProps> = ({
  sys,
  fields: { title, slides, slideContent, slideContentBackground },
  maxWidth,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef<any>(null);

  const handleSlideChange = (swiper: any) => {
    setActiveIndex(swiper.realIndex);
  };

  return (
    <RootContainer>
      <StyledTitle>{title}</StyledTitle>
      <CarouselWrapper $maxWidth={maxWidth}>
        <Swiper
          ref={swiperRef}
          modules={[FreeMode, Navigation, Keyboard, Mousewheel]}
          navigation={{
            nextEl: `.swiper-button-right-${sys.id}`,
            prevEl: `.swiper-button-left-${sys.id}`,
          }}
          onSlideChange={handleSlideChange}
          loop={slides.length >= 3}
          centeredSlides={slides.length >= 3}
          freeMode={{ enabled: true, sticky: true }}
          keyboard={{ enabled: true, onlyInViewport: true }}
          mousewheel={{ forceToAxis: true, sensitivity: 0.4 }}
          className="swiper-container"
          breakpoints={{
            0: { slidesPerView: 2.5, spaceBetween: 10 },
            1024: { slidesPerView: 4.5, spaceBetween: 50 },
          }}
        >
          {slides?.map((slide, index) => (
            <SwiperSlide key={`slide-${index}`}>
              {contentfulComponentLoop([slide])}
            </SwiperSlide>
          ))}
        </Swiper>
        <ArrowContainer>
          <RailArrow direction="left" railId={sys.id} />
          <RailArrow direction="right" railId={sys.id} />
        </ArrowContainer>
      </CarouselWrapper>

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
        <svg width="60" height="30">
          <polygon points="30,0 60,30 0,30" fill={slideContentBackground} />
        </svg>
      </div>
      <ContentContainer
        $activeIndex={activeIndex}
        $slideContentBackground={slideContentBackground}
      >
        {slideContent?.map((contentItem, index) => (
          <div key={`content-${index}`}>
            {contentfulComponentLoop([contentItem])}
          </div>
        ))}
      </ContentContainer>
    </RootContainer>
  );
};

export default DynamicContentCarousel;